/* StepFinish — Wizard's final step (NEW).
 * Shows a clean recap of what the seller configured + ONE big CTA that
 * routes them to /seller/catalog/import. The import page is the single
 * source of truth for source picking (Magic Link, Shopify, CSV, etc.) so
 * we don't duplicate those tiles here.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Check, ChevronLeft, ArrowRight, Sparkles, Settings2, Grid3x3, Table,
} from "lucide-react";

const TEMPLATE_META = {
  catalog:   { label: "Catalog",   icon: Grid3x3, desc: "Image-led product grid" },
  linesheet: { label: "Linesheet", icon: Table,   desc: "Data-dense table view" },
};

export default function StepFinish({ state, industryPreset, onBack }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  // Build a quick recap of what's been chosen across the wizard
  const tpl = TEMPLATE_META[state?.template_id] || TEMPLATE_META.catalog;

  const finishAndImport = async () => {
    setBusy(true);
    try {
      await api.post("/seller/catalog-setup/complete");
      toast.success("Setup complete · time to import");
      nav("/seller/catalog/import");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not finalise setup");
    } finally { setBusy(false); }
  };

  const skipImportForNow = async () => {
    setBusy(true);
    try {
      await api.post("/seller/catalog-setup/complete");
      toast.success("Setup complete");
      nav("/seller/catalog");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not finalise setup");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="setup-step-finish">
      <div className="text-[10px] mono uppercase tracking-[0.24em] text-foreground/55">Step 4 of 4 · Ready</div>
      <h1 className="display text-3xl mt-2 tracking-tight">You&apos;re configured</h1>
      <p className="text-sm text-foreground/60 mt-2 max-w-2xl">
        Your catalog is set up. Now bring your products in — we&apos;ll match the columns to your fields automatically.
      </p>

      {/* Recap */}
      <div className="mt-7 rounded-2xl border border-border bg-white p-5 space-y-3">
        <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55">Your choices</div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Recap label="Industry">
            <span className="font-medium">{industryPreset?.label || "Beauty"}</span>
            {(state?.subcategories || []).length > 0 && (
              <span className="text-[11px] text-foreground/55 ml-2">
                · {(state.subcategories || []).length} segment{(state.subcategories || []).length !== 1 ? "s" : ""}
              </span>
            )}
          </Recap>
          <Recap label="Template">
            <span className="inline-flex items-center gap-2">
              <tpl.icon className="w-3.5 h-3.5 text-foreground/55"/>
              <span className="font-medium">{tpl.label}</span>
              <span className="text-[11px] text-foreground/55">· {tpl.desc}</span>
            </span>
          </Recap>
        </div>

        <Recap label="Field visibility">
          <span className="inline-flex items-center gap-2 text-[12.5px]">
            <Settings2 className="w-3.5 h-3.5 text-foreground/55"/>
            <span>{state?.fields_step_completed ? "Saved" : "Defaults applied (you can fine-tune later)"}</span>
          </span>
        </Recap>
      </div>

      {/* Big CTA */}
      <div className="mt-7 rounded-2xl bg-gradient-to-br from-foreground to-foreground/90 text-background p-6" data-testid="finish-cta">
        <div className="flex items-start gap-4">
          <span className="w-10 h-10 rounded-lg bg-accent text-foreground grid place-items-center shrink-0">
            <Sparkles className="w-5 h-5"/>
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] mono uppercase tracking-[0.22em] opacity-70">Last step</div>
            <div className="font-medium text-lg mt-1">Bring your catalog in</div>
            <p className="text-[12.5px] opacity-80 mt-1 leading-relaxed">
              Pick from any source — CSV, Excel, Magic Link, Shopify, Google Sheet, PDF, Email-in, or Webhook. AI maps the columns to your configured fields.
            </p>
          </div>
        </div>
        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <button onClick={finishAndImport} disabled={busy} data-testid="finish-go-import"
            className="rounded-full bg-accent text-foreground hover:bg-accent/90 h-11 px-5 text-sm font-medium inline-flex items-center justify-center gap-1.5">
            {busy ? "Saving…" : <>Continue to import <ArrowRight className="w-3.5 h-3.5"/></>}
          </button>
          <button onClick={skipImportForNow} disabled={busy} data-testid="finish-skip-import"
            className="rounded-full border border-white/15 hover:border-white/40 text-background/80 hover:text-background h-11 px-5 text-sm">
            I&apos;ll import later
          </button>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button onClick={onBack} className="h-10 px-4 rounded-full text-sm text-foreground/65 hover:text-foreground inline-flex items-center gap-1.5"><ChevronLeft className="w-3.5 h-3.5"/>Back</button>
      </div>
    </div>
  );
}

function Recap({ label, children }) {
  return (
    <div className="rounded-lg bg-secondary/40 border border-border px-3 py-2.5">
      <div className="text-[9.5px] mono uppercase tracking-[0.22em] text-foreground/50">{label}</div>
      <div className="mt-1 text-[13px]">{children}</div>
    </div>
  );
}
